import { stepOtherTugoLookup as expenseLookup } from './claimCodeMapper';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire(data) {
  return [];
}

// Generic function to map any type of expense
function mapExpensesByType(data, itemsKey, expenseLookupKey, condition = true, claimTypes = []) {
  if (!condition || !data[itemsKey] || data[itemsKey].length < 1) {
    return [];
  }

  // Get claim code: prefer the claimCode set by configClaimCodeMapper (policy-aware),
  // then try to match against actual plan claim types from associatedClaimCodes.
  const lookup = expenseLookup[expenseLookupKey];
  let claimCode = null;
  if (lookup.claimCode) {
    // claimCode is set at startup by configClaimCodeMapper based on the actual policy
    claimCode = lookup.claimCode;
  } else if (lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0) {
    // Fallback: match against claim types available for this plan
    if (Array.isArray(claimTypes) && claimTypes.length > 0) {
      const availableCodes = claimTypes.map((ct) => ct.claimCode);
      claimCode = lookup.associatedClaimCodes.find((code) => availableCodes.includes(code)) || null;
    }
  }

  if (!claimCode) {
    return [];
  }
  const expenses = [];

  data[itemsKey].forEach((item) => {
    const expense = mapExpenseItem(item, data, claimCode);
    if (!!expense) {
      expenses.push(expense);
    }
  });

  return expenses;
}

function mapToExpenses(data, claimTypes) {
  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  const expenseConfigs = [
    {
      items: 'repatriationBenefitItems',
      lookup: 'repatriationBenefit',
      condition: expensesSelected.includes('Repatriation Benefit (Return of Remains)'),
    },
    {
      items: 'repatriationItems',
      lookup: 'repatriation',
      condition: expensesSelected.includes('Repatriation (Burial at place of death)'),
    },
    {
      items: 'returnHomeBenefitItems',
      lookup: 'returnHomeBenefit',
      condition: expensesSelected.includes('Return Home Benefit'),
    },
    {
      items: 'deathRemainsItems',
      lookup: 'deathRemains',
      condition: expensesSelected.includes('Return of remains to home country'),
    },
    {
      items: 'deathBurialItems',
      lookup: 'deathBurial',
      condition: expensesSelected.includes('Burial or Cremation at Place of Death'),
    },
    {
      items: 'deathTransportItems',
      lookup: 'deathTransport',
      condition: expensesSelected.includes('Transportation of Family Member to Identify Remains when required'),
    },
    {
      items: 'airFlightCommonCarrierAccidentItems',
      lookup: 'airFlightCommonCarrierAccident',
      condition: expensesSelected.includes('Air Flight/Common Carrier Accident Benefits'),
    },
    {
      items: 'accident24HourItems',
      lookup: 'accident24Hour',
      condition: expensesSelected.includes('24 Hour Accident Benefits'),
    },
  ];

  // Map all expenses using the generic function
  const allExpenses = expenseConfigs.flatMap((config) =>
    mapExpensesByType(data, config.items, config.lookup, config.condition, claimTypes)
  );

  return [...allExpenses];
}

function mapExpenseItem(item, itemDate, claimCode) {
  const selectedClaimForArray = (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor;
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  const list = [];
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))) {
    return list;
  }

  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  if (expensesSelected.length < 1 || claimTypes === undefined) {
    return list;
  }

  // Get all other expenses
  const otherExpenses = mapToExpenses(data, claimTypes);

  // Create mapping of claim codes to expense types
  // Use associatedClaimCodes to map all possible plan-specific codes to their descriptions
  const claimCodeMapping = {};
  const lookupEntries = [
    { key: 'repatriationBenefit', description: 'Repatriation Benefit (Return of Remains)' },
    { key: 'repatriation', description: 'Repatriation (Burial at place of death)' },
    { key: 'returnHomeBenefit', description: 'Return Home Benefit' },
    { key: 'deathRemains', description: 'Return of remains to home country' },
    { key: 'deathBurial', description: 'Burial or Cremation at Place of Death' },
    { key: 'deathTransport', description: 'Transportation of Family Member to Identify Remains when required' },
    { key: 'airFlightCommonCarrierAccident', description: 'Air Flight/Common Carrier Accident Benefits' },
    { key: 'accident24Hour', description: '24 Hour Accident Benefits' },
  ];
  lookupEntries.forEach(({ key, description }) => {
    const entry = expenseLookup[key];
    if (entry.claimCode) {
      claimCodeMapping[entry.claimCode] = description;
    }
    if (entry.associatedClaimCodes) {
      entry.associatedClaimCodes.forEach((code) => {
        claimCodeMapping[code] = description;
      });
    }
  });

  // Map each claim type to its corresponding expenses
  claimTypes.forEach((item) => {
    const expenseType = claimCodeMapping[item.claimCode];
    if (expenseType && expensesSelected.includes(expenseType)) {
      const benefit = mapClaimExpensesLevelItem(
        item,
        expenseType,
        otherExpenses.filter((expense) => expense.claimTypeCode === item.claimCode)
      );
      list.push(benefit);
    }
  });

  return list;
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepOtherDataTugoMapper.js